import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { usePick } from "@/hooks/usePick";
import { fmtDay, fmtTime, fmtDateTime, STATUS_LABEL, tzLabel } from "@/lib/format";
import GameCard from "@/components/GameCard";
import { DemoBadge, ErrorBox, Spinner } from "@/components/Bits";

export default function MatchDetail() {
  const { id } = useParams();
  const { leagueId, tz } = useAuth();
  const [games, setGames] = useState([]);
  const [error, setError] = useState("");
  const { pick, saving } = usePick(setGames);
  const load = useCallback(() => {
    if (!leagueId) return;
    api.get(`/games/${id}`, { params: { league_id: leagueId } }).then(({ data }) => setGames([data])).catch((e) => setError(errMsg(e)));
  }, [id, leagueId]);
  useEffect(() => { load(); }, [load]);
  const inPlay = games[0] && (games[0].status === "live" || (games[0].status === "scheduled" && new Date(games[0].start_time) <= new Date()));
  useEffect(() => {
    if (!inPlay) return undefined;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [inPlay, load]);

  const g = games[0];
  if (error) return (
    <div className="mpl-card p-6" data-testid="match-unavailable">
      <p className="font-display text-2xl font-extrabold uppercase">Match indisponible</p>
      <p className="mt-2 text-sm text-slate-400">Ce match n'appartient pas à la ligue sélectionnée, ou il s'agissait d'un ancien match de démonstration qui n'est plus disponible. Vos pronostics NBA réels ne sont pas touchés.</p>
      <Link to="/mes-predictions" className="mpl-btn-ghost mt-4 inline-flex" data-testid="match-unavailable-back">Retour à mes prédictions</Link>
    </div>
  );
  if (!g) return <Spinner />;
  return (
    <div className="space-y-6">
      <Link to="/matchs" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white" data-testid="match-back-link"><ArrowLeft size={15} /> Matchs</Link>
      <div className="mpl-rise">
        <p className="mpl-eyebrow capitalize">{fmtDay(g.start_time, tz)}</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold uppercase leading-tight sm:text-4xl">{g.away.name} <span className="text-slate-500">@</span> {g.home.name}</h1>
        <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-400">
          <Clock size={14} /> <span data-testid="match-local-time">{fmtTime(g.start_time, tz)}</span> ({tzLabel(tz)}) · <span data-testid="match-utc-time" className="font-mono-num text-xs">{new Date(g.start_time).toISOString().slice(11, 16)} UTC</span>
          {g.is_demo && <DemoBadge />}
        </p>
      </div>
      <GameCard game={g} tz={tz} onPick={pick} saving={saving === g.id} setGames={setGames} rule={g.rule} />
      {g.breakdown && (
        <section className="mpl-card p-4 text-sm" data-testid="match-breakdown">
          <h2 className="mb-2 text-sm font-bold">Total des points du match</h2>
          {[["Vainqueur", g.breakdown.winner], ["Questions bonus", g.breakdown.questions], ["Bonus de série", g.breakdown.streak]].map(([l, v]) => (
            <p key={l} className="flex justify-between text-xs text-slate-300"><span>{l}</span><b className={v > 0 ? "text-[#B7F52A]" : v < 0 ? "text-red-300" : "text-slate-400"}>{v > 0 ? `+${v}` : v}</b></p>
          ))}
          <p className="mt-2 flex justify-between border-t border-white/10 pt-2 font-bold"><span>Total</span><span className={g.breakdown.total >= 0 ? "text-[#B7F52A]" : "text-red-300"} data-testid="match-breakdown-total">{g.breakdown.total > 0 ? `+${g.breakdown.total}` : g.breakdown.total}</span></p>
        </section>
      )}
      {g.status === "live" && (
        <section className="mpl-card p-4" data-testid="match-live-leaders">
          <h2 className="mb-3 text-sm font-bold">En direct · meneurs du match</h2>
          {g.live_leaders?.pts ? (
            <div className="grid grid-cols-3 gap-3 text-xs">
              {[["pts", "Points"], ["reb", "Rebonds"], ["ast", "Passes"]].map(([k, l]) => (
                <div key={k} data-testid={`live-leader-${k}`}>
                  <p className="mpl-eyebrow">{l}</p>
                  <p className="mt-1 font-mono-num text-xl font-bold text-[#B7F52A]">{g.live_leaders[k][k]}</p>
                  <p className="text-slate-300">{g.live_leaders[k].name} <span className="text-slate-500">({g.live_leaders[k].team_abbr})</span></p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400" data-testid="live-leaders-unavailable">
              {g.live_box_scores_available ? "Statistiques en direct pas encore disponibles pour ce match." : "Le score s'actualise toutes les 30 secondes. Les meneurs en direct nécessitent l'accès aux feuilles de match en direct, non inclus dans l'abonnement actuel au fournisseur de données."}
            </p>
          )}
        </section>
      )}
      {g.picks && (
        <section className="mpl-card p-4" data-testid="match-picks">
          <h2 className="mb-3 text-sm font-bold">Choix des membres de la ligue</h2>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {["away", "home"].map((s) => (
              <div key={s}>
                <p className="mb-1 font-semibold text-white">{g[s].abbr} · {g.picks[s].length}</p>
                <p className="text-slate-400">{g.picks[s].join(", ") || "—"}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      {!g.locked && <p className="text-xs text-slate-500">Les choix des autres membres seront visibles au début du match.</p>}
      {g.history?.length > 0 && (
        <section className="mpl-card p-4" data-testid="match-result-history">
          <h2 className="mb-2 text-sm font-bold">Historique du résultat</h2>
          {g.history.map((h) => (
            <p key={h.id} className="text-xs text-slate-400">v{h.version} · {STATUS_LABEL[h.status]} {h.home_score != null && `${h.away_score}–${h.home_score}`} · {fmtDateTime(h.recorded_at, tz)}</p>
          ))}
        </section>
      )}
    </div>
  );
}
